import { FormCheckbox, FormDatePicker, FormInput, FormMonthRangePicker, FormSelect } from "@/components";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef, useState } from "react";
import { get, useFieldArray, useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { HEADER, INVOICES, LINE_ITEM } from "../../consts";
import { drcrOptions, transactionTypeOptions } from "@/lib/utils";
import useApi from "./useApi";
import { toast } from "@/components/Toast";
import { useAddMJEFormMutation, useGetLineItemsByVendorId, usePostDocMutation, useSubmitForApprovalDocMutation } from "@/services";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import moment from "moment";
import { 
  VENDOR_ID,
  PAYMENT_TERM_ID,
  BILLING_CATEGORY,
  ZONE_ID,
  BLOCK_ID,
  PROPERTY_ID,
  PROPERTY_OWNERSHIP_ID,
  GL_MASTER,
  TRANSACTION_TYPE,
  DRCR,
  LINE_ITEM_TYPE,
  BUDGET_YEAR,
  BUDGET_START_MONTH,
  BUDGET_END_MONTH,
  BALANCE_AMOUNT,
  isFieldSkipped,
  POSTING_DATE,
  POSTING_YEAR,
  POSTING_PERIOD,
  DUE_DATE,
  UNITS,
  RATE,
  AMOUNT,
  CURRENCY,
  ALT_GL_MASTER,
  BANK_ACC_GL,
  BANK_ACC_NAME,
  PAYMENT_METHOD,
  BANK_CHARGES,
  INVOICE_DATE,
  INVOICE_NO,
  INVOICE_REF,
  DOC_NO,
  DOC_TYPE,
  HEADER_TEXT,
  BASELINE_DATE,
  PAY_BLOCK,
  INVOICE_AMT,
  APPROVED_AMT,
  invoiceFields,
  LI_INVOICE_DATE,
  LI_INVOICE_NO,
  LI_INVOICE_REF,
  LI_DOC_NO,
  LI_DOC_TYPE,
  LI_HEADER_TEXT,
  LI_BASELINE_DATE,
  LI_DUE_DATE,
  LI_PAY_BLOCK,
  LI_DRCR,
  LI_INVOICE_AMT,
  LI_APPROVED_AMT
 } from "./consts";

const useManualEntry = ({configFields}: {configFields: any}) => {
  const {
    vendorList,
    paymentTermOptions,
    billingCategoryList,
    zoneList,
    blockList,
    propertyList,
    propertyOccupancyList,
    glData,
    glAccount,
    paymentMethodList,
    refetchPaymentMethod
  } = useApi({configFields});
  const isSubmitApprovalClicked = useRef<boolean>(false);
  const isPostClicked = useRef<boolean>(false);
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const [_, setUpdate] = useState<number>(Math.random() * 3);
  const selectedInvoice = useRef<string[]>([]);

  const headerFields = useMemo(() => configFields?.filter(
    (f: any) => f?.fieldLevel?.toLowerCase() == "header" && f.applicable
  ) || [], [configFields]);

  const lineItemFields = useMemo(() => configFields?.filter(
    (f: any) => f?.fieldLevel?.toLowerCase() == "line_item" && f.applicable
  ) || [], [configFields]);

  const generateSchema = (fields: any[]) => {
    const schemaShape: any = {};
    fields?.forEach((field) => {
      if (isFieldSkipped(field.fieldName)) return false;
      if (field.fieldName == "balanceAmount") return false;
      let validator: any = z.any();
      
      if (["STRING", "INTEGER", "DECIMAL", "ENUM", "LOOKUP"].includes(field.fieldDataType)) {
        validator = z.string();
        validator = field.required 
          ? validator.min(1, { message: `This field is required` }) 
          : validator.optional().or(z.literal(""));
      }

      if (field.fieldName == BUDGET_YEAR) validator = z.any();
      schemaShape[field.fieldName] = validator;
    });
    return schemaShape;
  };

  const formSchema = useMemo(() => {
    return z.object({
      // Wrap header fields in their own object schema
      [HEADER]: z.object(generateSchema(headerFields)),
      [INVOICES]: z.array(z.object(generateSchema(invoiceFields))),
      [LINE_ITEM]: z.array(z.object(generateSchema(lineItemFields)))
    });
  }, [headerFields, lineItemFields]);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      [HEADER]: headerFields.reduce((acc: any, field: any) => {
        let value = field.defaultValue || "";

        if (field.fieldName == CURRENCY) {
          value = 'USD';
        }

        acc[field.fieldName] = value || "";
        return acc;
      }, {}),
      [INVOICES]: [],
      [LINE_ITEM]: []
    }
  });

  const vendorId = useWatch({control: form.control, name: `${HEADER}.${VENDOR_ID}`})

  const getOptions = (key: string, isAlternate?: boolean, isBankGl?: boolean) => {
    switch (key) {
      case VENDOR_ID:
        return (vendorList || [])?.map((data: any) => ({ label: data?.vendorName, value: data?.vendorId?.toString() }));

      case PAYMENT_TERM_ID:
        return (paymentTermOptions?.data || [])?.map((data: any) => ({
          value: data?.paymentTermId?.toString(),
          label: data?.paymentTermName,
        }))

      case PAYMENT_METHOD:
        const selectedMethodId = vendorList?.find((data: any) => data?.vendorId == vendorId)?.paymentMethodId;
        return (paymentMethodList?.data || [])?.filter((data: any) => data?.paymentMethodId == selectedMethodId)?.map((data: any) => ({
          value: data?.paymentMethodId?.toString(),
          label: data?.methodName,
        }))

      case BILLING_CATEGORY:
        return (billingCategoryList?.data || [])?.map((data: any) => ({
          value: data?.billingCategoryId?.toString(),
          label: data?.categoryName,
        }))

      case TRANSACTION_TYPE:
        return transactionTypeOptions;
      
      case ZONE_ID:
        return (zoneList?.data || [])?.map((data: any) => ({
          value: data?.zoneId?.toString(),
          label: data?.zoneName,
        }))

      case BLOCK_ID:
        return (blockList?.data || [])?.map((data: any) => ({
          value: data?.blockId?.toString(),
          label: data?.blockName,
        }))

      case PROPERTY_ID:
        return (propertyList?.data || [])?.map((data: any) => ({
          value: data?.propertyId?.toString(),
          label: data?.name,
        }))

      case DRCR:
        return drcrOptions
      
      case PROPERTY_OWNERSHIP_ID:
        return (propertyOccupancyList?.data || [])?.map((data: any) => ({
          value: data?.occupancyId?.toString(),
          label: `${data?.firstName} ${data?.lastName}`,
        }))

      case GL_MASTER:
        const glDataList = (glAccount || [])?.map((data: any) => ({
          label: `${data?.glAccountNumber} - ${data?.accountShortDesc}`,
          value: data?.glMasterId?.toString()
        }))

        const alternateGlData = (glAccount || [])?.map((data: any) => ({
          label: `${data?.alternativeAccount}`,
          value: data?.glAccountNumber?.toString()
        }))

        const bankGl = (glAccount || [])?.map((data: any) => ({
          label: `${data?.glAccountNumber} - ${data?.accountShortDesc}`,
          value: data?.glMasterId?.toString()
        }))

        return isAlternate ? alternateGlData : isBankGl ? bankGl : glDataList;

      case BANK_ACC_GL:
        return (glAccount || [])?.map((data: any) => ({
          label: `${data?.glAccountNumber} - ${data?.accountShortDesc}`,
          value: data?.glMasterId?.toString()
        }))

      case ALT_GL_MASTER:
        return (glAccount || [])?.map((data: any) => ({
          label: `${data?.glAccountNumber}`,
          value: data?.glMasterId?.toString(),
        }))

      case LINE_ITEM_TYPE:
        return [{label: 'Line Item 1', value: '1'}];

      default:
        return [];
    }
  }

  const { data: lineItemsInfo, isRefetching: isLineItemsLoading, refetch: refetchLineItemInfo } =
    useGetLineItemsByVendorId({
      vendorId: vendorId as string,
      queryParams: {
        enabled: false,
      },
    });

    useEffect(() => {
      if (vendorId) {
        refetchLineItemInfo();
        refetchPaymentMethod();
      }
    }, [vendorId]);

    useEffect(() => {
      if (paymentMethodList?.data) {
        setUpdate(Math.random() * 2);
      }
    }, [paymentMethodList]);

    // Inside useManualEntry hook
  useEffect(() => {
    if (headerFields.length > 0) {
      const currentValues = form.getValues();
      let invoiceLineItem = [];
      
      // Create the updated header object
      const updatedHeader = headerFields.reduce((acc: any, field: any) => {
        // Priority: 
        // 1. Existing value in form 
        // 2. Hardcoded USD for Currency
        // 3. Field default value
        if (field.fieldName === CURRENCY) {
          acc[field.fieldName] = "USD";
        } else {
          acc[field.fieldName] = currentValues[HEADER]?.[field.fieldName]?.toString() || field.defaultValue?.toString() || "";
        }
        return acc;
      }, {});

      if (lineItemsInfo) {
        invoiceLineItem = lineItemsInfo?.data?.map((item: any) => {
          const keys = Object.keys(item);
          return invoiceFields.reduce((acc: any, field: any) => {
            const key = keys.find((val: any) => field.fieldName?.toLowerCase()?.includes(val?.toLowerCase()));
  
            let value = item[key as any]?.toString() ?? "";
  
            if (field.fieldName == BUDGET_YEAR && item?.[BUDGET_YEAR]) {
              value = {
                from: new Date(
                  item?.[BUDGET_YEAR],
                  Number(item?.[BUDGET_START_MONTH] - 1),
                  1
                ),
                to: new Date(
                  item?.[BUDGET_YEAR],
                  Number(item?.[BUDGET_END_MONTH] - 1),
                  1
                ),
              }
            } else if (field.fieldName == AMOUNT && item[UNITS] && item[RATE]) {
              value = (Number(item[UNITS]) * Number(item[RATE]))?.toString();
            } else if (field.fieldName == ALT_GL_MASTER) {
              value = item[GL_MASTER]?.toString()
            } else if (field.fieldName == PROPERTY_OWNERSHIP_ID) {
              value = item?.[PROPERTY_OWNERSHIP_ID]?.toString()
            } else if (field.fieldName == PAYMENT_TERM_ID) {
              value = item?.[PAYMENT_TERM_ID]?.toString()
            }
  
            acc[field.fieldName] = value ?? "";
            return acc;
            
          }, {...item});
        }) || [];
      }

      // Use reset to update the entire form state safely
      form.reset({
        ...currentValues,
        [HEADER]: updatedHeader,
        [INVOICES]: invoiceLineItem,
        [LINE_ITEM]: [],
      });
    }
  }, [headerFields, lineItemFields, lineItemsInfo]); // Trigger this when config fields arrive
  
  const onChangeText = (index: number, fieldName: string) => (e: any) => {
    const fName = index !== undefined 
      ? `${LINE_ITEM}.${index}.${fieldName}` 
      : `${HEADER}.${fieldName}`;
      
    form.setValue(fName as any, e.target.value, { 
      shouldValidate: true, 
      shouldDirty: true 
    });

    if (fieldName == UNITS || fieldName == RATE) {
      calculateAmount(
        fieldName == UNITS ? e.target.value : form.watch(`${LINE_ITEM}.${index}.${UNITS}` as any),
        fieldName == RATE ? e.target.value : form.watch(`${LINE_ITEM}.${index}.${RATE}` as any),
        `${LINE_ITEM}.${index}.${AMOUNT}`
      );
    }
  }

  const calculateAmount = (unit: number, rate: number, amountKey: string) => {
    const amt = unit * rate;
    form.setValue(amountKey as any, parseFloat(amt.toString()).toFixed(2));
  };

  const { fields: lineItemRows, append, remove } = useFieldArray({
    control: form.control,
    name: LINE_ITEM
  });

  const watchedLineItems = useWatch({
    control: form.control,
    name: LINE_ITEM
  });

  const watchedInvoices = useWatch({
    control: form.control,
    name: INVOICES, 
  });

  const balanceAmount = useMemo(() => {
    if (!Array.isArray(watchedLineItems)) return 0;

    // 2. Optimized single-pass calculation
    const totals = watchedLineItems?.reduce((acc, item) => {
      const amt = Number(item?.liApprovedAmount) || 0;
      const type = item?.[DRCR]?.toLowerCase();

      if (type == "dr") acc.dr += amt;
      if (type == "cr") acc.cr += amt;
      
      return acc;
    }, { dr: 0, cr: 0 });

    return totals.dr - totals.cr;
    
  }, [watchedLineItems]);

  const onChangeDate = () => {
    if (configFields?.[0]?.documentType == "VI") {
      onBaseLineDateChanged();
    }
    onPostingDateChanged();
  }

  const onBaseLineDateChanged = () => {
    const { header } = form.watch();
    const { baselineDate, vendorId } = header;

    const days = paymentTermOptions?.data?.find((term: any) => {
      const _paymentTerm = (vendorList || [])
        .find(
          (vendor: any) => vendor.vendorId?.toString() === vendorId?.toString()
        )
        ?.paymentTerm?.toString();

      return term?.paymentTermId?.toString() == _paymentTerm;
    })?.days;

    if (baselineDate && days) {
      form.setValue(`${HEADER}.${DUE_DATE}`, moment(baselineDate).add(days, "days"));
    }
  };

  const onPostingDateChanged = () => {
    const { header } = form.watch();
    const { postingDate } = header;

      form.setValue(`${HEADER}.${POSTING_YEAR}`, moment(postingDate).format('YYYY'));
      form.setValue(`${HEADER}.${POSTING_PERIOD}`, moment(postingDate).format('MMM'));
  };

  const onOptionChange = (index: number, fieldName: string) => (e: any)=> {
    const fName = index !== undefined 
      ? `${LINE_ITEM}.${index}.${fieldName}` 
      : `${HEADER}.${fieldName}`;
    
    form.setValue(fName as any, e, );

    if (fieldName == GL_MASTER) {
      form.setValue(`${LINE_ITEM}.${index}.${ALT_GL_MASTER}`, e, );
    } else if (fieldName == BANK_ACC_GL) {
      form.setValue(`${HEADER}.${BANK_ACC_NAME}`, glAccount?.find((data: any) => data.glMasterId == e)?.accountShortDesc || '')
    }
  }
  
  const renderDynamicField = (field: any, form: any, index?: number, isInvoice?: boolean) => {
    // If index is provided, it's a line item, so we prefix the name
    const fieldName = index !== undefined 
      ? isInvoice ? `${INVOICES}.${index}.${field.fieldName}` : `${LINE_ITEM}.${index}.${field.fieldName}` 
      : `${HEADER}.${field.fieldName}`; // Path for header fields

    const disabledVpLineItem = !isInvoice && form.watch(`${LINE_ITEM}.${index}.${LINE_ITEM_TYPE}`) == "MAIN"
    const isOffsetItem = !isInvoice && form.watch(`${LINE_ITEM}.${index}.isOffset`) && field.fieldName == 'liApprovedAmount'
    const isVpAmountField = [AMOUNT, LI_APPROVED_AMT].includes(field.fieldName);

    const {errors} = form.formState;
    const error = get(errors, fieldName);

    const commonProps = {
      name: fieldName,
      control: form.control,
      label: index !== undefined ? "" : field.displayLabel, // Hide labels in table rows usually
      isRequired: field.required,
      disabled: isOffsetItem ? false : !field.editable || field.fieldName == CURRENCY || field.fieldName == ALT_GL_MASTER || isInvoice || (disabledVpLineItem && !isVpAmountField),
      error: error?.message,
    };

    const isAmount = field?.fieldName?.toLowerCase()?.includes("amount");

    if (field.fieldName?.toLowerCase() == BUDGET_YEAR?.toLowerCase()) {
      const hasBudgetMonthStart = configFields?.some(
          (f: any) => f.fieldName?.toLowerCase() === BUDGET_START_MONTH?.toLowerCase() && f.applicable
        );
      const hasBudgetMonthEnd = configFields?.some(
          (f: any) => f.fieldName?.toLowerCase() === BUDGET_END_MONTH?.toLowerCase() && f.applicable
        );
      const allExists = hasBudgetMonthEnd && hasBudgetMonthStart;
        
      return allExists ? <FormMonthRangePicker {...commonProps} placeholder="Please select"/> : <></>
    }

    switch (field.fieldDataType?.toUpperCase()) {
      case "INTEGER":
        return <FormInput {...commonProps} variant="integer" placeholder="Please enter" startIcon={isAmount ? "$" : ""} onChange={onChangeText(index as number, field?.fieldName)} />;
      case "DECIMAL":
        return <FormInput {...commonProps} variant="float" placeholder="Please enter" startIcon={isAmount ? "$" : ""} onChange={onChangeText(index as number, field?.fieldName)} />;
      case "DATE":
        return <FormDatePicker {...commonProps} placeholder="Please select" onChange={onChangeDate} />;
      case "BOOLEAN":
        return <FormCheckbox {...commonProps} placeholder="Please select"/>;
      case "ENUM":
      case "LOOKUP":
        let options = [];
        try { 
          if (field.fieldName == GL_MASTER) {
            options = getOptions(field.fieldName, form.watch(`${LINE_ITEM}.${index}.isAtl`), form.watch(`${LINE_ITEM}.${index}.isBankGl`)); 
          } else {
            options = getOptions(field.fieldName); 
          }
        } catch { 
          options = []; 
        }
        return <FormSelect {...commonProps} options={options} placeholder="Please select" onChange={onOptionChange(index as number, field?.fieldName)} />;
      default:
        return <FormInput {...commonProps} placeholder="Please enter" onChange={onChangeText(index as number, field?.fieldName)} />;
    }
  };

  const addRow = () => {
    const emptyRow = lineItemFields.reduce((acc: any, field: any) => {
      acc[field.fieldName] = field.defaultValue || "";
      return acc;
    }, {isOffset: true});
    append(emptyRow);
  };

  const {
    mutate: submitForApprovalInfo,
    isPending: isSubmitPending
  } = useSubmitForApprovalDocMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
      return error?.response?.data?.message;
    },
    onSuccess: ({data}: any) => {
      if (data?.data?.errors) {
        const msg = data?.data?.errors?.map((err: any) => `${err.fieldCode ? `${err.fieldCode}: ` : ''}${err.errorMessage}`)
        toast.error(msg
        );
      } else {
        form.reset();
        toast.success(`Document ${data?.data?.parkDocumentHeaderId} Submitted for Approval Successfully`);
      }
    },
  });

  const {
    mutate: postInfo,
    isPending: isPostPending
  } = usePostDocMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
      return error?.response?.data?.message;
    },
    onSuccess: ({data}: any) => {
      if (data?.data?.errors) {
        const msg = data?.data?.errors?.map((err: any) => `${err.fieldCode ? `${err.fieldCode}: ` : ''}${err.errorMessage}`)
        toast.error(msg
        );
      } else {
        toast.success(`Document ${data?.data?.documentNumber} Posted Successfully`);
      }
    },
  });

  const {
    mutate: addMJEInfo,
    isPending: isAddPending
  } = useAddMJEFormMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
      return error?.response?.data?.message;
    },
    onSuccess: ({data}: any) => {
      if (data?.data?.errors) {
        const msg = data?.data?.errors?.map((err: any) => `${err.fieldCode ? `${err.fieldCode}: ` : ''}${err.errorMessage}`)
        toast.error(msg
        );
      } else {
        if (isSubmitApprovalClicked.current) {
          submitForApprovalInfo({
            communityId: communityData?.communityId,
            parkDocId: data?.data?.parkDocumentHeaderId
          })
        } else if (isPostClicked.current) {
          postInfo({
            communityId: communityData?.communityId,
            parkDocId: data?.data?.parkDocumentHeaderId as string,
          })
        } else {
          form.reset();
          toast.success("Manual Entry Created Successfully");
        }
      }
    },
  });

  const onSubmit = (data: any) => {
    Object.keys(data[HEADER])?.map((key: any) => {
      if (key?.toLowerCase()?.includes("date")) {
        data[HEADER][key] = data.header[key] ? moment(data.header[key]).format('YYYY-MM-DD') : null
      }

      if (key == 'postingPeriod') {
        data[HEADER][key] = data.header[POSTING_DATE] ? moment(data.header[POSTING_DATE]).format('MM') : null
      }
    })
    data[HEADER][BALANCE_AMOUNT] = balanceAmount;

    data[LINE_ITEM]?.map((item: any, index: number) => {
      Object.keys(item)?.map((key: any) => {
        if (key?.toLowerCase()?.includes("date")) {
          data[LINE_ITEM][index][key] = data[LINE_ITEM][index][key] ? moment(data[LINE_ITEM][index][key]).format('YYYY-MM-DD') : null
        }

        if (key?.toLowerCase() == BUDGET_YEAR?.toLowerCase()) {
          const budgetMonth = data[LINE_ITEM][index][BUDGET_YEAR];

          data[LINE_ITEM][index][BUDGET_YEAR] = budgetMonth
            ? moment(budgetMonth?.from).format("YYYY")
            : null;
          data[LINE_ITEM][index][BUDGET_START_MONTH] = budgetMonth
            ? moment(budgetMonth?.from).format("MM")
            : null;
            data[LINE_ITEM][index][BUDGET_END_MONTH] = budgetMonth
            ? moment(budgetMonth?.to).format("MM")
            : null;
        }
      })
      data[LINE_ITEM][index][LINE_ITEM_TYPE] = "MAIN";
    })

    const newData = {
      ...data
    }

    delete newData[INVOICES];

    addMJEInfo({
      data: {
        communityId: communityData?.communityId,
        documentTypeId: configFields?.[0]?.documentTypeId,
        clearingTargetIds: selectedInvoice?.current,
        ...newData, 
      }
    })
  }

  const onSubmitForApproval = () => {
    isSubmitApprovalClicked.current = true;
    isPostClicked.current = false;
  }

  const onPost = () => {
    isSubmitApprovalClicked.current = false;
    isPostClicked.current = true;
  }

  const onSelectInvoice = (checked: any, val: any) => {
    let baseItems = [];
    
    if (checked) {
      selectedInvoice.current?.push(val?.postDocumentHeaderId)
      // const alternateAcc = glAccount?.find((gl: any) => gl?.glMasterId == val?.glMasterId)
      const obj = {
        [LI_INVOICE_DATE]: val?.[INVOICE_DATE],
        [LI_INVOICE_NO]: val?.[INVOICE_NO],
        [LI_INVOICE_REF]: val?.[INVOICE_REF],
        [LI_DOC_NO]: val?.[DOC_NO],
        [LI_DOC_TYPE]: val?.[DOC_TYPE],
        [LI_HEADER_TEXT]: val?.[HEADER_TEXT],
        [LI_BASELINE_DATE]: val?.[BASELINE_DATE],
        [LI_DUE_DATE]: val?.[DUE_DATE],
        [LI_PAY_BLOCK]: val?.[PAY_BLOCK],
        [LI_DRCR]: val?.[DRCR],
        [LI_INVOICE_AMT]: val?.[INVOICE_AMT],
        [LI_APPROVED_AMT]: val?.[APPROVED_AMT],
        [PAYMENT_METHOD]: val?.[PAYMENT_METHOD]?.toString()
      }

      const glEntries = [
        {...val, ...obj, [LINE_ITEM_TYPE]: "MAIN"},
        {...val, ...obj, [GL_MASTER]: glData?.find((gl: any) => gl?.glMasterId == val?.glMasterId)?.glAccountNumber, [LINE_ITEM_TYPE]: "MAIN", [DRCR]: val?.[DRCR]?.toLowerCase() == "dr" ? "CR" : "DR", isAtl: true}
      ]
      baseItems = [...watchedLineItems, ...glEntries]
    } else {
      const idx = selectedInvoice?.current?.indexOf(val?.postDocumentHeaderId);
      if (idx > -1) selectedInvoice?.current?.splice(idx, 1);

      const li_item = watchedLineItems?.filter((data: any) => data?.postDocumentHeaderId != val?.postDocumentHeaderId);
      baseItems = [...li_item]
    }
    console.log("🚀 ~ onSelectInvoice ~ baseItems:", baseItems)

    // 3. Strip out any existing legacy bank rows before starting the math
    // const cleanItemsWithoutBank = baseItems?.filter((item: any) => !item?.isBankGl) || [];

    // // 4. Compute fresh mathematical aggregations from the clean base array list
    // const approvedAmount = cleanItemsWithoutBank
    //   ?.filter((data: any) => data?.liApprovedAmount)
    //   // FIX: Fallback to reading data.liApprovedAmount or item.approvedAmount safely
    //   ?.reduce((acc: any, item: any) => acc + Number(item?.liApprovedAmount || item?.approvedAmount || 0), 0) || 0;

    // 5. Construct fresh balancing offsets
    // const finalLineItems = [
    //   ...cleanItemsWithoutBank,
    //   ...(bankAccountGlId && approvedAmount > 0 ? [{
    //     [GL_MASTER]: bankAccountGlId,
    //     [LINE_ITEM_TYPE]: "MAIN",
    //     [DRCR]: "CR", 
    //     liApprovedAmount: String(approvedAmount + Number(bankCharges || 0)),
    //     isBankGl: true
    //   }] : []),
    //   ...(bankCharges && approvedAmount > 0 ? [{
    //     [GL_MASTER]: glData?.find((gl: any) => gl?.glAccountNumber === 402 || gl?.glAccountNumber === "402")?.glMasterId,
    //     [LINE_ITEM_TYPE]: "MAIN",
    //     [DRCR]: "DR", 
    //     liApprovedAmount: String(bankCharges),
    //     isBankGl: true
    //   }] : [])
    // ];

    // 6. Push down changes using deep flags to force the hook forms wrapper to draw new elements
    // form.setValue(LINE_ITEM, [...finalLineItems], { 
    //   shouldValidate: true, 
    //   shouldDirty: true,
    //   shouldTouch: true 
    // });
  }

  const bankAccountGlId = useWatch({control: form.control, name: `${HEADER}.${BANK_ACC_GL}`});
  const bankCharges = useWatch({control: form.control, name: `${HEADER}.${BANK_CHARGES}`});

  useEffect(() => {
    if (bankAccountGlId && watchedLineItems?.length > 0) {
      const approvedAmount = watchedLineItems?.filter((data: any) => data?.liApprovedAmount)?.reduce((acc: any, item: any) => acc + Number(item?.approvedAmount), 0);
      const glEntries = [
        ...watchedLineItems?.filter((item: any) => !item?.isBankGl), 
        {
          [GL_MASTER]: bankAccountGlId,
          [LINE_ITEM_TYPE]: "MAIN",
          [DRCR]: "CR", 
          liApprovedAmount: approvedAmount + Number(bankCharges || 0),
          isBankGl: true
        },
        ...(bankCharges ? [{
          [GL_MASTER]: glData?.find((gl: any) => gl?.glAccountNumber == 402)?.glMasterId,
          [LINE_ITEM_TYPE]: "MAIN",
          [DRCR]: "DR", 
          liApprovedAmount: Number(bankCharges),
          isBankGl: true
        }] : [])
      ]
      form.setValue(LINE_ITEM, glEntries);
    }
  }, [bankAccountGlId, bankCharges])

  return {
    form,
    renderDynamicField,
    headerFields,
    lineItemFields: lineItemFields.length ? [...lineItemFields, {fieldName: 'delete'}] : [],
    lineItemRows,
    addRow,
    removeRow: remove,
    isAddPending,
    onSubmit,
    balanceAmount,
    isLineItemsLoading,
    isSubmitPending,
    onSubmitForApproval,
    isPostPending,
    onPost,
    watchedInvoices: watchedInvoices?.length ? watchedInvoices : [],
    invoiceFields: invoiceFields.length ? [{fieldName: 'check'}, ...invoiceFields] : [],
    onSelectInvoice,
    vendorId
  };
};

export default useManualEntry;
